import * as React from 'react'

type UseZoomInputHandlersArgs = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  highlightIndex: number
  setHighlightIndex: (index: number) => void
  optionCount: number
  moveHighlight: (delta: number) => void
  activateOptionIndex: (index: number) => void
  commitDraft: () => void
  isDirty: boolean
  setIsDirty: (dirty: boolean) => void
  setDraft: (draft: string) => void
  dirtyFromInputChangeRef: React.MutableRefObject<boolean>
  resetDraftFromProps: () => void
  inputRef: React.RefObject<HTMLInputElement | null>
}

/** Event handlers for the zoom combobox input and its chevron toggle. */
export function useZoomInputHandlers({
  open,
  setOpen,
  highlightIndex,
  setHighlightIndex,
  optionCount,
  moveHighlight,
  activateOptionIndex,
  commitDraft,
  isDirty,
  setIsDirty,
  setDraft,
  dirtyFromInputChangeRef,
  resetDraftFromProps,
  inputRef,
}: UseZoomInputHandlersArgs) {
  /** Select entire value so the next typed digit replaces it (typical zoom / numeric combobox UX). */
  const selectZoomInputAll = React.useCallback(() => {
    const el = inputRef.current

    if (!el) return

    requestAnimationFrame(() => {
      el.select()
    })
  }, [inputRef])

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (open && highlightIndex >= 0) {
        activateOptionIndex(highlightIndex)
        return
      }

      commitDraft()
      inputRef.current?.blur()
      return
    }

    if (!open || optionCount === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      moveHighlight(1)
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      moveHighlight(-1)
    }
  }

  const handleInputFocus = () => {
    setOpen(true)
    selectZoomInputAll()
  }

  const handleInputClick = () => {
    setOpen(true)
    selectZoomInputAll()
  }

  const handleInputMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    if (e.button !== 0) return
    const el = e.currentTarget

    if (document.activeElement === el) {
      e.preventDefault()
      el.select()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirty(true)
    dirtyFromInputChangeRef.current = true
    setDraft(e.target.value)
    setOpen(true)
    setHighlightIndex(-1)
  }

  const handleInputBlur = () => {
    if (dirtyFromInputChangeRef.current) {
      commitDraft()
      return
    }

    if (isDirty) {
      resetDraftFromProps()
    }
  }

  const handleToggleClick = () => {
    setOpen((prev) => {
      const nextOpen = !prev

      requestAnimationFrame(() => {
        if (nextOpen) {
          inputRef.current?.focus()
        } else {
          inputRef.current?.blur()
        }
      })
      return nextOpen
    })
  }

  return {
    handleInputKeyDown,
    handleInputFocus,
    handleInputClick,
    handleInputMouseDown,
    handleInputChange,
    handleInputBlur,
    handleToggleClick,
  }
}
